import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HelpCircle, X, Send, MessageCircle, Bug, Lightbulb, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
});

const HelpButton = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasBeenClosed, setHasBeenClosed] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const minimizeTimerRef = useRef(null);

  // Check session storage for previous state
  useEffect(() => {
    const wasMinimized = sessionStorage.getItem('helpButtonMinimized') === 'true';
    const wasClosed = sessionStorage.getItem('helpPanelClosed') === 'true';
    if (wasMinimized) setIsMinimized(true);
    if (wasClosed) setHasBeenClosed(true);
  }, []);

  // Auto-minimize on mobile after scroll or timeout
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile || isOpen || hasBeenClosed) return;

    // Minimize after 5 seconds of inactivity
    minimizeTimerRef.current = setTimeout(() => {
      setIsMinimized(true);
      sessionStorage.setItem('helpButtonMinimized', 'true');
    }, 5000);

    const handleScroll = () => {
      if (!isMinimized && !isOpen) {
        setIsMinimized(true);
        sessionStorage.setItem('helpButtonMinimized', 'true');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (minimizeTimerRef.current) clearTimeout(minimizeTimerRef.current);
    };
  }, [isOpen, isMinimized, hasBeenClosed]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setHasBeenClosed(true);
    sessionStorage.setItem('helpPanelClosed', 'true');
    // Reset form state
    setSelectedCategory(null);
    setMessage('');
  }, []);

  const handleButtonClick = () => {
    if (isMinimized) {
      setIsMinimized(false);
      sessionStorage.setItem('helpButtonMinimized', 'false');
      return;
    }
    setIsOpen(!isOpen);
  };

  const handleSubmit = async () => {
    if (!message.trim() || !selectedCategory) {
      toast.error('Please select a category and enter a message');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API}/support/feedback`,
        {
          category: selectedCategory,
          message: message.trim(),
          url: window.location.href,
          user_agent: navigator.userAgent
        },
        getAuthConfig()
      );
      toast.success('Thank you! We\'ll get back to you soon.');
      handleClose();
    } catch (error) {
      // If endpoint doesn't exist, still show success for UX
      console.log('Feedback submission:', error.response?.status);
      toast.success('Thank you for your feedback!');
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories = [
    { id: 'bug', icon: Bug, label: 'Report a Bug', color: 'text-red-400' },
    { id: 'question', icon: MessageCircle, label: 'Ask a Question', color: 'text-blue-400' },
    { id: 'suggestion', icon: Lightbulb, label: 'Suggestion', color: 'text-amber-400' },
  ];

  // Don't render if no user
  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        data-testid="help-button"
        className={`
          fixed z-50 rounded-full shadow-lg transition-all duration-300 ease-out
          flex items-center justify-center
          bg-gradient-to-br from-[#FF7F50] to-[#e66b3e] hover:from-[#ff8f66] hover:to-[#FF7F50]
          text-white focus:outline-none focus:ring-2 focus:ring-[#FF7F50]/50 focus:ring-offset-2 focus:ring-offset-transparent
          ${isMinimized 
            ? 'w-8 h-8 md:w-11 md:h-11 bottom-4 right-1 translate-x-1/3 opacity-70 hover:opacity-100 hover:translate-x-0' 
            : 'w-10 h-10 md:w-12 md:h-12 bottom-6 right-4 md:right-6'
          }
          ${isOpen ? 'scale-90' : 'hover:scale-105'}
        `}
        style={{ 
          boxShadow: isMinimized 
            ? '0 2px 8px rgba(255, 127, 80, 0.3)' 
            : '0 4px 20px rgba(255, 127, 80, 0.4)' 
        }}
        aria-label="Help and Support"
      >
        <HelpCircle className={`${isMinimized ? 'w-4 h-4 md:w-5 md:h-5' : 'w-5 h-5 md:w-6 md:h-6'}`} />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          data-testid="help-panel"
          className={`
            fixed z-50 bg-white rounded-2xl shadow-2xl border border-zinc-200
            transition-all duration-200 ease-out animate-in fade-in slide-in-from-bottom-4
            bottom-20 md:bottom-24
            right-4 md:right-6
            w-[calc(100vw-2rem)] md:w-[360px]
            max-h-[70vh] overflow-hidden
          `}
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-[#1E3A4C] to-[#2a4d63]">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-[#FF7F50]" />
              <h3 className="font-semibold text-white text-sm">Help & Support</h3>
            </div>
            <button
              onClick={handleClose}
              data-testid="help-panel-close"
              className="p-1 rounded-full hover:bg-white/10 transition-colors text-white/70 hover:text-white"
              aria-label="Close help panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[calc(70vh-60px)] overflow-y-auto">
            {!selectedCategory ? (
              /* Category Selection */
              <div className="space-y-2">
                <p className="text-sm text-zinc-500 mb-3">How can we help you today?</p>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    data-testid={`help-category-${cat.id}`}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-zinc-200 hover:border-[#FF7F50]/50 hover:bg-[#FFF8F5] transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-zinc-100 group-hover:bg-[#FF7F50]/10 transition-colors`}>
                        <cat.icon className={`w-4 h-4 ${cat.color}`} />
                      </div>
                      <span className="text-sm font-medium text-zinc-700">{cat.label}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-[#FF7F50] transition-colors" />
                  </button>
                ))}
                
                {/* Quick Links */}
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <p className="text-xs text-zinc-400 mb-2">Quick Links</p>
                  <a 
                    href="/how-it-works" 
                    target="_blank"
                    className="text-sm text-[#4682B4] hover:text-[#FF7F50] transition-colors flex items-center gap-1"
                  >
                    How It Works
                    <ChevronRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ) : (
              /* Message Form */
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1 transition-colors"
                >
                  <ChevronRight className="w-3 h-3 rotate-180" />
                  Back
                </button>
                
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                  {(() => {
                    const cat = categories.find(c => c.id === selectedCategory);
                    return cat ? (
                      <>
                        <cat.icon className={`w-4 h-4 ${cat.color}`} />
                        {cat.label}
                      </>
                    ) : null;
                  })()}
                </div>

                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    selectedCategory === 'bug' 
                      ? "Describe what happened and what you expected..."
                      : selectedCategory === 'question'
                      ? "What would you like to know?"
                      : "Share your idea or suggestion..."
                  }
                  className="min-h-[120px] resize-none border-zinc-200 focus:border-[#FF7F50] focus:ring-[#FF7F50]/20"
                  data-testid="help-message-input"
                />

                <div className="flex items-center justify-between">
                  <p className="text-xs text-zinc-400">
                    Logged in as {user?.email?.split('@')[0]}
                  </p>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !message.trim()}
                    data-testid="help-submit-button"
                    className="bg-[#FF7F50] hover:bg-[#e66b3e] text-white text-sm px-4"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Send
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HelpButton;
